/**
 * Enforce2FAGate — real enforcement of the org's "Require 2FA for
 * administrators" security setting (2026-06-16 overhaul).
 *
 * Mounted once in MainLayout (the admin shell). When ALL of:
 *   - the org has `require_2fa_for_admins` enabled (settings.security), AND
 *   - the current user's role is an administrator tier, AND
 *   - the user has NOT enrolled 2FA (svdy-authorization /2fa/status)
 * are true, it renders a blocking backdrop that the admin can only clear
 * by enrolling in two-factor authentication (reusing the existing
 * TwoFactorSetupDialog). Frontline workers are never gated.
 *
 * Safety: fail-OPEN. Any error reading the policy or the 2FA status leaves
 * the app usable rather than risking locking a legitimate admin out — the
 * worst case of a missed check is an admin not being forced to enroll, far
 * preferable to a false lockout. This mirrors the defense-in-depth posture
 * in session-discipline §12 (the gate is the front control; not blocking on
 * uncertainty is the backstop against lockout).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Backdrop, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { getCurrentRoleCode, systemApi } from '../../api';
import { isAdminTierRole } from '../../hooks/useRoleLanding';
import { twoFactorApi } from '../../api/twofa';
import TwoFactorSetupDialog from './TwoFactorSetupDialog';

const Enforce2FAGate: React.FC = () => {
  const { t } = useTranslation();
  const [blocked, setBlocked] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);

  const evaluate = useCallback(async () => {
    const role = getCurrentRoleCode();
    if (!isAdminTierRole(role)) {
      setBlocked(false);
      return;
    }
    try {
      const [sec, status] = await Promise.all([
        systemApi.systemSettingsApi.getSecurity(),
        twoFactorApi.status(),
      ]);
      const required = !!(sec?.data as { require_2fa_for_admins?: boolean } | undefined)
        ?.require_2fa_for_admins;
      setBlocked(required && !status.enrolled);
    } catch {
      // Fail-open: never lock an admin out on a transient read failure.
      setBlocked(false);
    }
  }, []);

  useEffect(() => { evaluate(); }, [evaluate]);

  if (!blocked) {
    return (
      <TwoFactorSetupDialog
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onEnrolled={() => { setSetupOpen(false); evaluate(); }}
      />
    );
  }

  return (
    <>
      <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal + 2, bgcolor: 'rgba(15,23,42,0.72)' }}>
        <Paper elevation={0} sx={{ maxWidth: 440, p: 4, borderRadius: 2, textAlign: 'center', mx: 2 }}>
          <Stack spacing={2} alignItems="center">
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(0,113,227,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LockOutlined sx={{ fontSize: 28, color: 'primary.main' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
              {t('system:security.gateTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('system:security.gateBody')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setSetupOpen(true)}
              sx={{ borderRadius: 1, textTransform: 'none', mt: 0.5 }}
            >
              {t('system:security.gateButton')}
            </Button>
          </Stack>
        </Paper>
      </Backdrop>
      <TwoFactorSetupDialog
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onEnrolled={() => { setSetupOpen(false); evaluate(); }}
      />
    </>
  );
};

export default Enforce2FAGate;
